import { ChangeEvent, useState, FocusEvent } from 'react'
import MailAddress from './assets/mail-address.png'
import ContactPhone from './assets/contact-phone.png'
import ErrorInfo from './assets/error-info.png'
import { sanitizeString } from '@mohalla-tech/xss-safeguard'
import queryString from 'query-string'

const FORM_STATES = {
  INITIAL: 'INITIAL',
  SUCCESS: 'SUCCESS',
  FAIL: 'FAIL'
}
const disclaimers = [
  'It may take us some time to process your account deletion request.',
  'You may download a copy of your data. We will contact you for the same as per the details provided at the time of submission of your account deletion request.',
  'You can cancel your account deletion request within 30 days of submission of the request by simply logging in.',
  'After 30 days of your account deletion request, you will not be able to retrieve your account data.',
  'Your data will be retained for a period of 180 days (or more) as per applicable legal requirements, post the submission of your account deletion request.'
]

export const _sanitizeStr = (str: string) => sanitizeString(str)

export const _sanitizeInput = (
  str: string,
  expression = /[?;:<>{}[\]\\/]/gi
) => (str ? str.replace(expression, '') : str)

const sanitizeData = (values: { [key: string]: string }) => {
  const sanitizedValues = { ...values }
  Object.keys(sanitizedValues).forEach((key) => {
    if (typeof sanitizedValues[key] === 'string') {
      sanitizedValues[key] = _sanitizeInput(sanitizedValues[key])
    }
  })
  return sanitizedValues
}

function AccountDeletion() {
  const [form, setForm] = useState({
    email: '',
    phoneNo: '',
    reason: ''
  })
  const [reasonStrCount, setReasonStrCount] = useState(0)
  const [emailError, setEmailError] = useState(true)
  const [phoneNoError, setPhoneNoError] = useState(true)
  const [emailErrorMsg, setEmailErrorMsg] = useState(false)
  const [phoneNoErrorMsg, setPhoneNoErrorMsg] = useState(false)
  const [formState, setFormState] = useState(FORM_STATES.INITIAL)
  let msg = 'Your request has been successfully registered.'
  let Wrapper = <></>

  const emailValidation = (email: string) =>
    /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)

  const phoneNumberValidation = (phoneNo: string) =>
    /^[0-9]+$/.test(phoneNo) && phoneNo.length === 10

  const handleChange = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>
  ) => {
    const key = e.target.name
    const val = e.target.value

    if (key === 'phoneNo') {
      if (val.length <= 10) {
        setData(key, val)
        setPhoneNoError(!phoneNumberValidation(val))
      }
    } else {
      if (key === 'email') {
        setEmailError(!emailValidation(val))
      } else if (val.length <= 300) {
        setReasonStrCount(val.length)
      }
      setData(key, val)
    }
  }

  const handleError = (e: FocusEvent<HTMLInputElement>) => {
    const key = e.target.name
    const val = e.target.value
    if (key === 'email') {
      setEmailErrorMsg(!emailValidation(val))
    } else if (key === 'phoneNo') {
      if (val.length <= 10) {
        setPhoneNoErrorMsg(!phoneNumberValidation(val))
      }
    }
  }

  const setData = (key: string, value: string) =>
    setForm((formData) => ({
      ...formData,
      [key]: value
    }))

  const handleSubmit = () => {
    const queryObj = queryString.parse(window.location.search)
    const sanitizedFormData = sanitizeData(form)
    const payload = {
      ...sanitizedFormData,
      UserId: _sanitizeStr(queryObj.userid as string),
      DeviceId: _sanitizeStr(queryObj.deviceid as string)
    }
    fetch(
      'content-m8n-service/external/v1.0.0/data-deletion/users/deactivate',
      {
        method: 'POST',
        body: JSON.stringify({ ...payload, noNesting: true, allowToken: true })
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw Error(res.statusText)
        }
        return res.json()
      })
      .then(() => {
        setFormState(FORM_STATES.SUCCESS)
        const NativePayload = { type: 'DeleteFlow', subType: 'DeleteAccount' }
        if (window.Android) {
          window?.Android?.onAction?.(JSON.stringify(NativePayload))
        } else if (window.webkit) {
          window?.webkit?.messageHandlers?.onAction?.postMessage?.({
            jsonObject: JSON.stringify(NativePayload)
          })
        } else {
          console.log('PAYLOAD', NativePayload)
        }
      })
      .catch(() => {
        setFormState(FORM_STATES.FAIL)
      })
  }

  switch (formState) {
    case FORM_STATES.SUCCESS:
      Wrapper = (
        <div className="px-4 text-center font-bold text-black">{msg}</div>
      )
      break
    case FORM_STATES.FAIL:
      msg =
        'Not able to submit the request at this moment, please try again later.'
      Wrapper = (
        <div className="px-4 text-center font-bold text-black">{msg}</div>
      )
      break
    case FORM_STATES.INITIAL:
    default:
      Wrapper = (
        <div className="flex flex-col">
          <div className="px-4 py-2 text-[14px] text-[#74747b]">DETAILS</div>
          <label className="flex items-center">
            <img src={MailAddress} alt="Mail Address" className="size-[44px]" />
            <input
              type="email"
              className="h-[44px] grow border-b border-b-[#ddd] text-[16px] focus:outline-none"
              maxLength={42}
              name="email"
              onChange={handleChange}
              onBlur={handleError}
              value={form.email}
            />
            <div className={`-ml-8 ${!emailErrorMsg && 'hidden'}`}>
              <img src={ErrorInfo} alt="Error" className="mr-3 size-5" />
            </div>
          </label>
          <div
            className={`bg-white p-4 pt-1.5 text-right text-[12px] text-[#ff3b30] ${
              !emailErrorMsg && 'hidden'
            }`}
          >
            Invalid Mail id
          </div>
          <label className="flex items-center">
            <img
              src={ContactPhone}
              alt="Phone Number"
              className="size-[44px]"
            />
            <input
              type="number"
              className="h-[44px] grow border-b border-b-[#ddd] text-[16px] [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              name="phoneNo"
              onChange={handleChange}
              onBlur={handleError}
              value={form.phoneNo}
            />
            <div className={`-ml-8 ${!emailErrorMsg && 'hidden'}`}>
              <img src={ErrorInfo} alt="Error" className="mr-3 size-5" />
            </div>
          </label>
          <div
            className={`bg-white p-4 pt-1.5 text-right text-[12px] text-[#ff3b30] ${
              !phoneNoErrorMsg && 'hidden'
            }`}
          >
            Invalid Phone number
          </div>
          <div className="px-4 py-1.5 text-[14px] text-[#74747b]">
            Fill up the details to delete the account
          </div>
          <div className="px-4 pb-1.5 pt-[44px] text-[14px] text-[#74747b]">
            REASON
          </div>
          <div className="border-b border-b-[#ddd]" />
          <label className="grow px-4 pt-4">
            <textarea
              className="h-[144px] w-full grow rounded-lg border-none p-4 text-[16px] focus:outline-none"
              placeholder="I want to delete my account."
              maxLength={300}
              name="reason"
              onChange={handleChange}
              value={form.reason}
            />
            <div className="py-2 text-right text-[14px] text-[#74747b]">
              {reasonStrCount}/300
            </div>
          </label>
          <div className="mx-4 rounded-lg bg-white px-4 py-3">
            <span className="text-[#ff5a7e]"> Please Note: </span>
            <ol className="m-0 p-4 text-[12px]">
              {disclaimers.map((disclaimer, i) => (
                <li key={i}>{disclaimer}</li>
              ))}
            </ol>
            <span className="text-[13px]">
              To know more, <b>please refer to the FAQ section.</b>
            </span>
          </div>

          <div className="my-4 bg-white px-4 py-3">
            <a
              href="/delete-account-faq"
              className="flex justify-between text-black"
            >
              <div>FAQ</div>
              <div>
                {/* <SvgArrowDown
									width="14"
									height="16"
									className="Rotate(-90deg)"
								/> */}
              </div>
            </a>
          </div>
          <div className="fixed bottom-0 left-1/2 w-full max-w-[600px] -translate-x-1/2 px-4 py-3">
            <div
              className={`${
                emailError || phoneNoError
                  ? 'bg-[#ddd] text-[#32323E]'
                  : 'bg-[#577EFB] text-white'
              } m-auto inline-block w-full cursor-pointer rounded px-4 py-3.5 text-center text-[14px] font-bold`}
              onClick={emailError || phoneNoError ? () => {} : handleSubmit}
            >
              Submit
            </div>
          </div>
        </div>
      )
      break
  }

  return (
    <div className="top-0 mx-auto size-full min-h-[90vh]  bg-[#f2f2f3] pt-[48px]">
      <div className="relative mx-auto max-w-[600px] bg-[#f2f2f3] pb-[72px]">
        {Wrapper}
      </div>
    </div>
  )
}

export default AccountDeletion
